const moment = require('moment');

const join = (rules) => (value, data) => rules.map(rule => rule(value, data)).filter(error => !!error)[0 /* first error */];

export const isEmpty = value => value === undefined || value === null || value === '';

export function isNotBeforeToday(value) {
    if (moment(value).isBefore(moment(), 'day')) {
        return `Date cannot be in the past`;
    }
}

export function email(value) {
    if (!isEmpty(value) && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}$/i.test(value)) {
        return 'Invalid email address';
    }
}

export function phone(value) {
    if (value && (value.length < 5 || !/^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(value))) {
        return 'Invalid phone number';
    }
}

/**
 * Validate phone number in E.164 format
 * @param {string} value - phone number
 * @returns {string | undefined} - error message or undefined
 */
export function phoneE164(value) {
    if (value && !/^\+\d{1,15}$/.test(value)) {
        return 'Invalid international phone number (example: +[country code][number])';
    }
}

export function required(value) {
    if (isEmpty(value)) {
        return 'Required';
    }
}

export function minLength(min) {
    return value => {
        if (!isEmpty(value) && value.length < min) {
            return `Must be at least ${min} characters`;
        }
    };
}

export function maxLength(max) {
    return value => {
        if (!isEmpty(value) && value.length > max) {
            return `Must be no more than ${max} characters`;
        }
    };
}

export function integer(value) {
    if (isEmpty(value)) {
        return undefined;
    }
    if (!Number.isInteger(Number(value))) {
        return 'Must be a number';
    }
    if (!Number.isSafeInteger(Number(value))) {
        return `Must be smaller than ${Number.MAX_SAFE_INTEGER || '9007199254740992'}`;
    }
}

export const min = (minValue) => (value) => {
    const numberValue = Number(value);

    // Check if the value is a number
    if (isNaN(numberValue)) {
        return 'Must be a number';
    }

    // Check minimum value
    if (numberValue < minValue) {
        return `Must be at least ${minValue}`;
    }

    // If check passes, return undefined (no error)
    return undefined;
};

export const max = (maxValue) => (value) => {
    const numberValue = Number(value);

    // Check if the value is a number
    if (isNaN(numberValue)) {
        return 'Must be a number';
    }

    // Check maximum value
    if (numberValue > maxValue) {
        return `Must be no more than ${maxValue}`;
    }

    // If check passes, return undefined (no error)
    return undefined;
};

export function oneOf(enumeration) {
    return value => {
        if (!~enumeration.indexOf(value)) {
            return `Must be one of: ${enumeration.join(', ')}`;
        }
    };
}

export function match(field) {
    return (value, data) => {
        if (data) {
            if (value !== data[field]) {
                return 'Do not match';
            }
        }
    };
}

export function isURL(value) {
    if (!isEmpty(value) && !/^https?:\/\//.test(value)) {
        return "URL must begin with 'http' or 'https'";
    }
}

export const isSecureURL = (value) => {
    if (!isEmpty(value) && !/^https:\/\//.test(value)) {
        return "URL must begin with 'https'";
    }
}

export function isIP(value) {
    if (!isEmpty(value) && !/^(?!0)(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}$/.test(value)) {
        return "Must be a valid IPv4 address";
    }
}

export const normalizeInteger = value => value && parseInt(value);
export const normalizeNumber = value => value && Number(value);
export const normalizeEmail = value => value && `${value}`.trim().toLowerCase();
export const normalizeLowerCase = normalizeEmail;

export function createValidator(rules) {
    return (data = {}) => {
        const errors = {};
        Object.keys(rules).forEach((key) => {
            const rule = join([].concat(rules[key])); // concat enables both functions and arrays of functions
            const error = rule(data[key], data);
            if (error) {
                errors[key] = error;
            }
        });
        return errors;
    };
}
